import { X, Ruler, Activity, Zap, Upload, Image as ImageIcon, Trash2, FileText } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fileUploadAPI } from '../../services/api';

const StudentMeasurementModal = ({
  isOpen,
  onClose,
  onSave,
  editingMeasurement,
  saving,
  student
}) => {
  const { colors, isDark } = useTheme();
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('body');
  const [uploading, setUploading] = useState(false);
  const [measurementData, setMeasurementData] = useState({
    date: moment().format('YYYY-MM-DD'),
    weight: '',
    height: '',
    shoulder: '',
    chest: '',
    waist: '',
    bellyButton: '',
    hip: '',
    thighs: '',
    leftLeg: '',
    rightLeg: '',
    leftArm: '',
    rightArm: '',
    bmi: '',
    waistHipRatio: '',
    caliperTriceps: '',
    caliperSuprailiac: '',
    caliperThigh: '',
    caliperBodyFatPercentage: '',
    tanitaBodyFatPercentage: '',
    tanitaBodyFatKg: '',
    tanitaMusclePercentage: '',
    tanitaMuscleKg: '',
    frontImageUrl: '',
    backImageUrl: '',
    leftImageUrl: '',
    rightImageUrl: '',
    tanitaPhotoUrl: '',
    tanitaPdfUrl: ''
  });

  const [studentAge, setStudentAge] = useState('');
  const [studentGender, setStudentGender] = useState('male');

  useEffect(() => {
    if (editingMeasurement) {
      setMeasurementData({
        date: moment(editingMeasurement.date).format('YYYY-MM-DD'),
        weight: editingMeasurement.weight || '',
        height: editingMeasurement.height || '',
        shoulder: editingMeasurement.shoulder || '',
        chest: editingMeasurement.chest || '',
        waist: editingMeasurement.waist || '',
        bellyButton: editingMeasurement.bellyButton || '',
        hip: editingMeasurement.hip || '',
        thighs: editingMeasurement.thighs || '',
        leftLeg: editingMeasurement.leftLeg || '',
        rightLeg: editingMeasurement.rightLeg || '',
        leftArm: editingMeasurement.leftArm || '',
        rightArm: editingMeasurement.rightArm || '',
        bmi: editingMeasurement.bmi || '',
        waistHipRatio: editingMeasurement.waistHipRatio || '',
        caliperTriceps: editingMeasurement.caliperTriceps || '',
        caliperSuprailiac: editingMeasurement.caliperSuprailiac || '',
        caliperThigh: editingMeasurement.caliperThigh || '',
        caliperBodyFatPercentage: editingMeasurement.caliperBodyFatPercentage || '',
        tanitaBodyFatPercentage: editingMeasurement.tanitaBodyFatPercentage || '',
        tanitaBodyFatKg: editingMeasurement.tanitaBodyFatKg || '',
        tanitaMusclePercentage: editingMeasurement.tanitaMusclePercentage || '',
        tanitaMuscleKg: editingMeasurement.tanitaMuscleKg || '',
        frontImageUrl: editingMeasurement.frontImageUrl || '',
        backImageUrl: editingMeasurement.backImageUrl || '',
        leftImageUrl: editingMeasurement.leftImageUrl || '',
        rightImageUrl: editingMeasurement.rightImageUrl || '',
        tanitaPhotoUrl: editingMeasurement.tanitaPhotoUrl || '',
        tanitaPdfUrl: editingMeasurement.tanitaPdfUrl || ''
      });

      setStudentAge(editingMeasurement.age || '');
      setStudentGender(editingMeasurement.gender || 'male');
    } else {
      setMeasurementData({
        date: moment().format('YYYY-MM-DD'),
        weight: '',
        height: '',
        shoulder: '',
        chest: '',
        waist: '',
        bellyButton: '',
        hip: '',
        thighs: '',
        leftLeg: '',
        rightLeg: '',
        leftArm: '',
        rightArm: '',
        bmi: '',
        waistHipRatio: '',
        caliperTriceps: '',
        caliperSuprailiac: '',
        caliperThigh: '',
        caliperBodyFatPercentage: '',
        tanitaBodyFatPercentage: '',
        tanitaBodyFatKg: '',
        tanitaMusclePercentage: '',
        tanitaMuscleKg: '',
        frontImageUrl: '',
        backImageUrl: '',
        leftImageUrl: '',
        rightImageUrl: '',
        tanitaPhotoUrl: '',
        tanitaPdfUrl: ''
      });
      setActiveTab('body');
      setStudentAge('');
      setStudentGender('male');
    }
  }, [editingMeasurement, isOpen]);

  const getCalculatedAge = () => {
    if (student?.age) return student.age;
    if (student?.birthDate) {
      return moment().diff(moment(student.birthDate), 'years');
    }
    return studentAge;
  };

  const calculateJacksonPollockBodyFat = (triceps, suprailiac, thigh, age, gender = 'male') => {
    const tricepsVal = parseFloat(triceps);
    const suprailiacVal = parseFloat(suprailiac);
    const thighVal = parseFloat(thigh);
    const ageVal = parseFloat(age);

    if (!tricepsVal || !suprailiacVal || !thighVal || !ageVal) return null;

    const sumOfSkinfolds = tricepsVal + suprailiacVal + thighVal;
    let bodyDensity;

    const isMale = gender?.toLowerCase() === 'male' || gender === t('studentDetails.values.male');

    if (isMale) {
      bodyDensity = 1.10938 - (0.0008267 * sumOfSkinfolds) + (0.0000016 * Math.pow(sumOfSkinfolds, 2)) - (0.0002574 * ageVal);
    } else {
      bodyDensity = 1.0994921 - (0.0009929 * sumOfSkinfolds) + (0.0000023 * Math.pow(sumOfSkinfolds, 2)) - (0.0001392 * ageVal);
    }

    const bodyFatPercentage = ((4.95 / bodyDensity) - 4.5) * 100;

    return bodyFatPercentage.toFixed(1);
  };

  const handleInputChange = (field, value) => {
    setMeasurementData(prev => ({
      ...prev,
      [field]: value
    }));

    if (field === 'weight' || field === 'height') {
      const weight = field === 'weight' ? parseFloat(value) : parseFloat(measurementData.weight);
      const height = field === 'height' ? parseFloat(value) : parseFloat(measurementData.height);

      if (weight && height && height > 0) {
        const bmi = weight / ((height / 100) ** 2);
        setMeasurementData(prev => ({
          ...prev,
          bmi: bmi.toFixed(1)
        }));
      }
    }

    if (field === 'waist' || field === 'hip') {
      const waist = field === 'waist' ? parseFloat(value) : parseFloat(measurementData.waist);
      const hip = field === 'hip' ? parseFloat(value) : parseFloat(measurementData.hip);

      if (waist && hip && hip > 0) {
        const ratio = waist / hip;
        setMeasurementData(prev => ({
          ...prev,
          waistHipRatio: ratio.toFixed(2)
        }));
      }
    }

    if (field === 'caliperTriceps' || field === 'caliperSuprailiac' || field === 'caliperThigh' || field === 'studentAge' || field === 'studentGender') {
      const triceps = field === 'caliperTriceps' ? value : measurementData.caliperTriceps;
      const suprailiac = field === 'caliperSuprailiac' ? value : measurementData.caliperSuprailiac;
      const thigh = field === 'caliperThigh' ? value : measurementData.caliperThigh;
      
      const currentAge = field === 'studentAge' ? value : getCalculatedAge();
      const currentGender = field === 'studentGender' ? value : (student?.gender || studentGender);

      const bodyFat = calculateJacksonPollockBodyFat(triceps, suprailiac, thigh, currentAge, currentGender);
      if (bodyFat) {
        setMeasurementData(prev => ({
          ...prev,
          caliperBodyFatPercentage: bodyFat
        }));
      }
    }
  };

  const handleFileUpload = async (event, fieldName) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.type.match('image.*')) {
      alert(t('common.errors.onlyImages'));
      return;
    }

    setUploading(true);
    try {
      // 1. Get presigned URL
      const response = await fileUploadAPI.getPresignedUrl(file.type);
      // The API returns { data: { uploadUrl, imageUrl, key } }
      // The backend wraps it in 'data' key, so response.data.data
      const { uploadUrl, imageUrl } = response.data.data;

      // 2. Upload to S3
      await fileUploadAPI.uploadFile(uploadUrl, file, file.type);

      // 3. Use the returned imageUrl
      setMeasurementData(prev => ({
        ...prev,
        [fieldName]: imageUrl
      }));

    } catch (error) {
      console.error('Upload failed:', error);
      alert(t('common.errors.uploadFailed'));
    } finally {
      setUploading(false);
    }
  };

  // Generic file upload handler - supports images and PDFs
  const handleGenericFileUpload = async (event, fieldName, acceptTypes = ['image/*', 'application/pdf']) => {
    const file = event.target.files[0];
    if (!file) return;

    const isImage = file.type.match('image.*');
    const isPdf = file.type === 'application/pdf';

    if (!isImage && !isPdf) {
      alert(t('studentProfile.measurements.modal.tanita.invalidFileType'));
      return;
    }

    setUploading(true);
    try {
      const response = await fileUploadAPI.getPresignedUrl(file.type);
      const { uploadUrl, imageUrl } = response.data.data;
      await fileUploadAPI.uploadFile(uploadUrl, file, file.type);

      setMeasurementData(prev => ({
        ...prev,
        [fieldName]: imageUrl
      }));
    } catch (error) {
      console.error('Upload failed:', error);
      alert(t('common.errors.uploadFailed'));
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveImage = (fieldName) => {
    setMeasurementData(prev => ({
      ...prev,
      [fieldName]: ''
    }));
  };

  const ImageUploadBox = ({ label, fieldName, imageUrl }) => (
    <div className={`border border-dashed ${colors.border} rounded ${isDark ? 'bg-zinc-900/40' : 'bg-slate-50'} p-4 flex flex-col items-center justify-center text-center space-y-2 ${colors.bgHover} transition-colors relative group min-h-[140px]`}>
      {imageUrl ? (
        <>
          <img
            src={imageUrl}
            alt={label}
            className="w-full h-[120px] object-cover rounded absolute inset-0 p-1"
          />
          <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center rounded">
            <button
              onClick={() => handleRemoveImage(fieldName)}
              className="p-2 bg-red-600 text-white rounded shadow-lg hover:bg-red-700 transition-colors"
              title={t('common.remove')}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </>
      ) : (
        <>
          <div className={`w-10 h-10 rounded-full ${colors.bgTertiary} flex items-center justify-center mb-1`}>
            {uploading ? (
              <div className={`animate-spin rounded-full h-4 w-4 border-2 ${isDark ? 'border-zinc-400 border-t-zinc-100' : 'border-slate-300 border-t-slate-600'}`}></div>
            ) : (
              <Activity className={`w-4 h-4 ${colors.textTertiary}`} />
            )}
          </div>
          <div>
            <span className={`block text-[11px] font-bold ${colors.textSecondary} uppercase tracking-widest`}>
              {label}
            </span>
          </div>
          <input
            type="file"
            accept="image/*"
            onChange={(e) => handleFileUpload(e, fieldName)}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
            disabled={uploading}
          />
        </>
      )}
    </div>
  );

  const handleSave = () => {
    const dataToSave = {
      ...measurementData,
      age: studentAge || null,
      gender: studentGender,
      weight: measurementData.weight || null,
      height: measurementData.height || null,
      shoulder: measurementData.shoulder || null,
      chest: measurementData.chest || null,
      waist: measurementData.waist || null,
      bellyButton: measurementData.bellyButton || null,
      hip: measurementData.hip || null,
      thighs: measurementData.thighs || null,
      leftLeg: measurementData.leftLeg || null,
      rightLeg: measurementData.rightLeg || null,
      leftArm: measurementData.leftArm || null,
      rightArm: measurementData.rightArm || null,
      bmi: measurementData.bmi || null,
      waistHipRatio: measurementData.waistHipRatio || null,
      caliperTriceps: measurementData.caliperTriceps || null,
      caliperSuprailiac: measurementData.caliperSuprailiac || null,
      caliperThigh: measurementData.caliperThigh || null,
      caliperBodyFatPercentage: measurementData.caliperBodyFatPercentage || null,
      tanitaBodyFatPercentage: measurementData.tanitaBodyFatPercentage || null,
      tanitaBodyFatKg: measurementData.tanitaBodyFatKg || null,
      tanitaMusclePercentage: measurementData.tanitaMusclePercentage || null,
      tanitaMuscleKg: measurementData.tanitaMuscleKg || null,
      frontImageUrl: measurementData.frontImageUrl || null,
      backImageUrl: measurementData.backImageUrl || null,
      leftImageUrl: measurementData.leftImageUrl || null,
      rightImageUrl: measurementData.rightImageUrl || null,
      tanitaPhotoUrl: measurementData.tanitaPhotoUrl || null,
      tanitaPdfUrl: measurementData.tanitaPdfUrl || null
    };

    onSave(dataToSave);
  };

  if (!isOpen) return null;

  const tabs = [
    { id: 'body', name: t('studentProfile.measurements.modal.tabs.body'), icon: Ruler },
    { id: 'caliper', name: t('studentProfile.measurements.modal.tabs.caliper'), icon: Activity },
    { id: 'tanita', name: t('studentProfile.measurements.modal.tabs.tanita'), icon: Zap }
  ];

  const renderBodyMeasurements = () => (
    <div className="space-y-8 animate-in fade-in duration-300 pb-10">
      {/* Genel Bilgiler */}
      <section>
        <div className={`flex items-center justify-between pb-2 mb-6 border-b ${colors.border}`}>
          <h4 className={`text-sm font-bold ${colors.textPrimary}`}>Genel Bilgiler</h4>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="md:col-span-2">
            <label className={`block text-[11px] font-bold ${colors.textSecondary} mb-2 uppercase tracking-tight`}>Ölçüm Tarihi</label>
            <input
              type="date"
              value={measurementData.date}
              onChange={(e) => handleInputChange('date', e.target.value)}
              className={`w-full px-3 py-2 ${colors.inputBg} ${colors.inputBorder} ${colors.inputText} rounded text-sm focus:border-violet-500 focus:outline-none transition-colors transition-colors`}
            />
          </div>
          <div>
            <label className={`block text-[11px] font-bold ${colors.textSecondary} mb-2 uppercase tracking-tight`}>Kilo (kg)</label>
            <input
              type="number"
              value={measurementData.weight}
              onChange={(e) => handleInputChange('weight', e.target.value)}
              className={`w-full px-3 py-2 ${colors.inputBg} ${colors.inputBorder} ${colors.inputText} rounded text-sm focus:border-violet-500 focus:outline-none transition-colors shadow-sm`}
              step="0.1"
            />
          </div>
          <div>
            <label className={`block text-[11px] font-bold ${colors.textSecondary} mb-2 uppercase tracking-tight`}>Boy (cm)</label>
            <input
              type="number"
              value={measurementData.height}
              onChange={(e) => handleInputChange('height', e.target.value)}
              className={`w-full px-3 py-2 ${colors.inputBg} ${colors.inputBorder} ${colors.inputText} rounded text-sm focus:border-violet-500 focus:outline-none transition-colors shadow-sm`}
              step="0.1"
            />
          </div>
        </div>
      </section>

      {/* Gövde Ölçüleri */}
      <section>
        <div className={`flex items-center justify-between pb-2 mb-6 border-b ${colors.border}`}>
          <h4 className={`text-sm font-bold ${colors.textPrimary}`}>Gövde Ölçümleri</h4>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
          <div>
            <label className={`block text-[11px] font-bold ${colors.textSecondary} mb-2 uppercase tracking-tight`}>Omuz (cm)</label>
            <input
              type="number"
              value={measurementData.shoulder}
              onChange={(e) => handleInputChange('shoulder', e.target.value)}
              className={`w-full px-3 py-2 ${colors.inputBg} ${colors.inputBorder} ${colors.inputText} rounded text-sm focus:border-violet-500 focus:outline-none transition-colors shadow-sm`}
              step="0.1"
            />
          </div>
          <div>
            <label className={`block text-[11px] font-bold ${colors.textSecondary} mb-2 uppercase tracking-tight`}>Göğüs (cm)</label>
            <input
              type="number"
              value={measurementData.chest}
              onChange={(e) => handleInputChange('chest', e.target.value)}
              className={`w-full px-3 py-2 ${colors.inputBg} ${colors.inputBorder} ${colors.inputText} rounded text-sm focus:border-violet-500 focus:outline-none transition-colors shadow-sm`}
              step="0.1"
            />
          </div>
          <div>
            <label className={`block text-[11px] font-bold ${colors.textSecondary} mb-2 uppercase tracking-tight`}>Bel (cm)</label>
            <input
              type="number"
              value={measurementData.waist}
              onChange={(e) => handleInputChange('waist', e.target.value)}
              className={`w-full px-3 py-2 ${colors.inputBg} ${colors.inputBorder} ${colors.inputText} rounded text-sm focus:border-violet-500 focus:outline-none transition-colors shadow-sm`}
              step="0.1"
            />
          </div>
          <div>
            <label className={`block text-[11px] font-bold ${colors.textSecondary} mb-2 uppercase tracking-tight`}>Kalça (cm)</label>
            <input
              type="number"
              value={measurementData.hip}
              onChange={(e) => handleInputChange('hip', e.target.value)}
              className={`w-full px-3 py-2 ${colors.inputBg} ${colors.inputBorder} ${colors.inputText} rounded text-sm focus:border-violet-500 focus:outline-none transition-colors shadow-sm`}
              step="0.1"
            />
          </div>
          <div>
            <label className={`block text-[11px] font-bold ${colors.textSecondary} mb-2 uppercase tracking-tight`}>Göbek Çevresi</label>
            <input
              type="number"
              value={measurementData.bellyButton}
              onChange={(e) => handleInputChange('bellyButton', e.target.value)}
              className={`w-full px-3 py-2 ${colors.inputBg} ${colors.inputBorder} ${colors.inputText} rounded text-sm focus:border-violet-500 focus:outline-none transition-colors shadow-sm`}
              step="0.1"
            />
          </div>
          <div>
            <label className={`block text-[11px] font-bold ${colors.textSecondary} mb-2 uppercase tracking-tight`}>Uyluklar (cm)</label>
            <input
              type="number"
              value={measurementData.thighs}
              onChange={(e) => handleInputChange('thighs', e.target.value)}
              className={`w-full px-3 py-2 ${colors.inputBg} ${colors.inputBorder} ${colors.inputText} rounded text-sm focus:border-violet-500 focus:outline-none transition-colors shadow-sm`}
              step="0.1"
            />
          </div>
          <div className="bg-emerald-500/5 border border-emerald-500/20 p-2 rounded">
            <label className={`block text-[10px] font-bold text-emerald-500 mb-0.5 uppercase`}>BMI</label>
            <div className={`text-lg font-bold text-emerald-500`}>{measurementData.bmi || '--.-'}</div>
          </div>
          <div className="bg-violet-500/5 border border-violet-500/20 p-2 rounded">
            <label className={`block text-[10px] font-bold text-violet-500 mb-0.5 uppercase`}>Bel/Kalça</label>
            <div className={`text-lg font-bold text-violet-500`}>{measurementData.waistHipRatio || '--.--'}</div>
          </div>
        </div>
      </section>

      {/* Kol ve Bacaklar */}
      <section>
        <div className={`flex items-center justify-between pb-2 mb-6 border-b ${colors.border}`}>
          <h4 className={`text-sm font-bold ${colors.textPrimary}`}>Kol ve Bacak Ölçümleri</h4>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <div>
            <label className={`block text-[11px] font-bold ${colors.textSecondary} mb-2 uppercase tracking-tight`}>Sol Kol (cm)</label>
            <input
              type="number"
              value={measurementData.leftArm}
              onChange={(e) => handleInputChange('leftArm', e.target.value)}
              className={`w-full px-3 py-2 ${colors.inputBg} ${colors.inputBorder} ${colors.inputText} rounded text-sm focus:border-violet-500 focus:outline-none transition-colors shadow-sm`}
              step="0.1"
            />
          </div>
          <div>
            <label className={`block text-[11px] font-bold ${colors.textSecondary} mb-2 uppercase tracking-tight`}>Sağ Kol (cm)</label>
            <input
              type="number"
              value={measurementData.rightArm}
              onChange={(e) => handleInputChange('rightArm', e.target.value)}
              className={`w-full px-3 py-2 ${colors.inputBg} ${colors.inputBorder} ${colors.inputText} rounded text-sm focus:border-violet-500 focus:outline-none transition-colors shadow-sm`}
              step="0.1"
            />
          </div>
          <div>
            <label className={`block text-[11px] font-bold ${colors.textSecondary} mb-2 uppercase tracking-tight`}>Sol Bacak (cm)</label>
            <input
              type="number"
              value={measurementData.leftLeg}
              onChange={(e) => handleInputChange('leftLeg', e.target.value)}
              className={`w-full px-3 py-2 ${colors.inputBg} ${colors.inputBorder} ${colors.inputText} rounded text-sm focus:border-violet-500 focus:outline-none transition-colors shadow-sm`}
              step="0.1"
            />
          </div>
          <div>
            <label className={`block text-[11px] font-bold ${colors.textSecondary} mb-2 uppercase tracking-tight`}>Sağ Bacak (cm)</label>
            <input
              type="number"
              value={measurementData.rightLeg}
              onChange={(e) => handleInputChange('rightLeg', e.target.value)}
              className={`w-full px-3 py-2 ${colors.inputBg} ${colors.inputBorder} ${colors.inputText} rounded text-sm focus:border-violet-500 focus:outline-none transition-colors shadow-sm`}
              step="0.1"
            />
          </div>
        </div>
      </section>

      {/* Görsel Yükleme */}
      <section>
        <div className={`flex items-center justify-between pb-2 mb-6 border-b ${colors.border}`}>
          <h4 className={`text-sm font-bold ${colors.textPrimary}`}>
            Görsel Yükleme <span className={`text-[10px] ${colors.textTertiary} font-medium ml-2 font-normal uppercase`}>({t('studentProfile.measurements.modal.photos.optional')})</span>
          </h4>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <ImageUploadBox
            label={t('studentProfile.measurements.modal.photos.front')}
            fieldName="frontImageUrl"
            imageUrl={measurementData.frontImageUrl}
          />
          <ImageUploadBox
            label={t('studentProfile.measurements.modal.photos.back')}
            fieldName="backImageUrl"
            imageUrl={measurementData.backImageUrl}
          />
          <ImageUploadBox
            label={t('studentProfile.measurements.modal.photos.left')}
            fieldName="leftImageUrl"
            imageUrl={measurementData.leftImageUrl}
          />
          <ImageUploadBox
            label={t('studentProfile.measurements.modal.photos.right')}
            fieldName="rightImageUrl"
            imageUrl={measurementData.rightImageUrl}
          />
        </div>
      </section>
    </div>
  );

  const renderCaliperMeasurements = () => (
    <div className="space-y-8 animate-in fade-in duration-300 pb-10">
      <section>
        <div className={`flex items-center justify-between pb-2 mb-6 border-b ${colors.border}`}>
          <h4 className={`text-sm font-bold ${colors.textPrimary}`}>Caliper Ölçümü</h4>
        </div>
        
        <div className="grid grid-cols-2 gap-6 mb-8">
          <div>
            <label className={`block text-xs font-medium ${colors.textSecondary} mb-1.5 uppercase tracking-wider`}>Yaş</label>
            <input
              type="number"
              value={studentAge || getCalculatedAge()}
              onChange={(e) => {
                const val = e.target.value;
                setStudentAge(val);
                // Trigger calculation update
                const bf = calculateJacksonPollockBodyFat(
                  measurementData.caliperTriceps,
                  measurementData.caliperSuprailiac,
                  measurementData.caliperThigh,
                  val,
                  studentGender
                );
                setMeasurementData(prev => ({ ...prev, caliperBodyFatPercentage: bf }));
              }}
              className={`w-full px-3 py-2 ${colors.inputBg} ${colors.inputBorder} ${colors.inputText} rounded text-sm focus:border-violet-500 focus:outline-none transition-colors shadow-sm`}
              placeholder="Yaş giriniz..."
            />
          </div>
          <div>
            <label className={`block text-xs font-medium ${colors.textSecondary} mb-1.5 uppercase tracking-wider`}>Cinsiyet</label>
            <select
              value={studentGender}
              onChange={(e) => {
                const val = e.target.value;
                setStudentGender(val);
                // Trigger calculation update
                const bf = calculateJacksonPollockBodyFat(
                  measurementData.caliperTriceps,
                  measurementData.caliperSuprailiac,
                  measurementData.caliperThigh,
                  studentAge || getCalculatedAge(),
                  val
                );
                setMeasurementData(prev => ({ ...prev, caliperBodyFatPercentage: bf }));
              }}
              className={`w-full px-3 py-2 ${colors.inputBg} ${colors.inputBorder} ${colors.inputText} rounded text-sm focus:border-violet-500 focus:outline-none transition-colors appearance-none shadow-sm`}
            >
              <option value="male">ERKEK</option>
              <option value="female">KADIN</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label className={`block text-[11px] font-bold ${colors.textSecondary} mb-2 uppercase tracking-tight`}>Triceps (mm)</label>
            <input
              type="number"
              value={measurementData.caliperTriceps}
              onChange={(e) => handleInputChange('caliperTriceps', e.target.value)}
              className={`w-full px-3 py-2 ${colors.inputBg} ${colors.inputBorder} ${colors.inputText} rounded text-sm focus:border-violet-500 focus:outline-none transition-colors shadow-sm`}
              step="0.1"
            />
          </div>
          <div>
            <label className={`block text-xs font-medium ${colors.textSecondary} mb-1.5`}>Suprailiac (mm)</label>
            <input
              type="number"
              value={measurementData.caliperSuprailiac}
              onChange={(e) => handleInputChange('caliperSuprailiac', e.target.value)}
              className={`w-full px-3 py-2 ${colors.inputBg} ${colors.inputBorder} ${colors.inputText} rounded text-sm focus:border-violet-500 focus:outline-none transition-colors shadow-sm`}
              step="0.1"
            />
          </div>
          <div>
            <label className={`block text-xs font-medium ${colors.textSecondary} mb-1.5`}>Uyluk (mm)</label>
            <input
              type="number"
              value={measurementData.caliperThigh}
              onChange={(e) => handleInputChange('caliperThigh', e.target.value)}
              className={`w-full px-3 py-2 ${colors.inputBg} ${colors.inputBorder} ${colors.inputText} rounded text-sm focus:border-violet-500 focus:outline-none transition-colors shadow-sm`}
              step="0.1"
            />
          </div>
        </div>

        <div className={`mt-8 p-4 ${isDark ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-emerald-50 border-emerald-200'} border rounded flex items-center justify-between`}>
          <div>
            <h5 className="text-[10px] font-bold text-emerald-500 uppercase tracking-widest">Hesaplanan Vücut Yağ Oranı</h5>
            <p className={`text-[10px] ${colors.textTertiary} mt-1 uppercase`}>Jackson-Pollock 3 Bölge Formülü</p>
          </div>
          <div className="text-2xl font-black text-emerald-500">
            %{measurementData.caliperBodyFatPercentage || '--.-'}
          </div>
        </div>
      </section>

      <div className={`p-4 ${colors.bgTertiary} border ${colors.border} rounded`}>
        <p className={`text-xs ${colors.textTertiary} leading-relaxed uppercase tracking-wider font-medium`}>
          {t('studentProfile.measurements.modal.caliper.infoDesc')}
        </p>
      </div>
    </div>
  );

  const renderTanitaMeasurements = () => (
    <div className="space-y-8 animate-in fade-in duration-300 pb-10">
      <section>
        <div className={`flex items-center justify-between pb-2 mb-6 border-b ${colors.border}`}>
          <h4 className={`text-sm font-bold ${colors.textPrimary}`}>Tanita Ölçümü</h4>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <div>
            <label className={`block text-xs font-medium ${colors.textSecondary} mb-1.5`}>Yağ Oranı (%)</label>
            <input
              type="number"
              value={measurementData.tanitaBodyFatPercentage}
              onChange={(e) => handleInputChange('tanitaBodyFatPercentage', e.target.value)}
              className={`w-full px-3 py-2 ${colors.inputBg} ${colors.inputBorder} ${colors.inputText} rounded text-sm focus:border-violet-500 focus:outline-none transition-colors shadow-sm`}
              step="0.1"
            />
          </div>
          <div>
            <label className={`block text-xs font-medium ${colors.textSecondary} mb-1.5`}>Yağ Kütlesi (kg)</label>
            <input
              type="number"
              value={measurementData.tanitaBodyFatKg}
              onChange={(e) => handleInputChange('tanitaBodyFatKg', e.target.value)}
              className={`w-full px-3 py-2 ${colors.inputBg} ${colors.inputBorder} ${colors.inputText} rounded text-sm focus:border-violet-500 focus:outline-none transition-colors shadow-sm`}
              step="0.1"
            />
          </div>
          <div>
            <label className={`block text-xs font-medium ${colors.textSecondary} mb-1.5`}>Kas Oranı (%)</label>
            <input
              type="number"
              value={measurementData.tanitaMusclePercentage}
              onChange={(e) => handleInputChange('tanitaMusclePercentage', e.target.value)}
              className={`w-full px-3 py-2 ${colors.inputBg} ${colors.inputBorder} ${colors.inputText} rounded text-sm focus:border-violet-500 focus:outline-none transition-colors shadow-sm`}
              step="0.1"
            />
          </div>
          <div>
            <label className={`block text-xs font-medium ${colors.textSecondary} mb-1.5`}>Kas Kütlesi (kg)</label>
            <input
              type="number"
              value={measurementData.tanitaMuscleKg}
              onChange={(e) => handleInputChange('tanitaMuscleKg', e.target.value)}
              className={`w-full px-3 py-2 ${colors.inputBg} ${colors.inputBorder} ${colors.inputText} rounded text-sm focus:border-violet-500 focus:outline-none transition-colors shadow-sm`}
              step="0.1"
            />
          </div>
        </div>
      </section>

      <div className={`p-4 ${colors.bgTertiary} border ${colors.border} rounded`}>
        <p className={`text-xs ${colors.textTertiary} leading-relaxed uppercase tracking-wider font-medium`}>
          {t('studentProfile.measurements.modal.tanita.infoDesc')}
        </p>
      </div>

      {/* Tanita File Uploads */}
      <section>
        <div className={`flex items-center justify-between pb-2 mb-6 border-b ${colors.border}`}>
          <h4 className={`text-sm font-bold ${colors.textPrimary}`}>Rapor Yükleme</h4>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Tanita Photo */}
          <div className={`relative group min-h-[140px] border border-dashed ${colors.border} rounded-lg flex flex-col items-center justify-center p-4 ${colors.bgHover} transition-colors`}>
            {measurementData.tanitaPhotoUrl ? (
              <div className="w-full h-full relative">
                <img src={measurementData.tanitaPhotoUrl} alt="Tanita" className="w-full h-[120px] object-cover rounded" />
                <button onClick={() => handleRemoveImage('tanitaPhotoUrl')} className="absolute top-1 right-1 p-1.5 bg-red-500 text-white rounded shadow-lg"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            ) : (
              <div className="text-center">
                <ImageIcon className={`w-6 h-6 ${colors.textTertiary} mx-auto mb-2`} />
                <span className={`text-[10px] font-bold ${colors.textTertiary} uppercase tracking-widest block`}>Analiz Görseli</span>
                <input type="file" accept="image/*" onChange={(e) => handleGenericFileUpload(e, 'tanitaPhotoUrl')} className="absolute inset-0 opacity-0 cursor-pointer" />
              </div>
            )}
          </div>

          {/* Tanita PDF */}
          <div className={`relative group min-h-[140px] border border-dashed ${colors.border} rounded-lg flex flex-col items-center justify-center p-4 ${colors.bgHover} transition-colors`}>
            {measurementData.tanitaPdfUrl ? (
              <div className="flex flex-col items-center">
                <FileText className="w-8 h-8 text-red-500 mb-2" />
                <span className={`text-[10px] font-bold ${colors.textTertiary} uppercase tracking-widest mb-2`}>PDF Rapor Yüklendi</span>
                <div className="flex gap-2">
                  <a href={measurementData.tanitaPdfUrl} target="_blank" rel="noopener noreferrer" className="text-[10px] font-bold text-blue-500 hover:text-blue-400 underline uppercase tracking-widest">Görüntüle</a>
                  <button onClick={() => handleRemoveImage('tanitaPdfUrl')} className="text-[10px] font-bold text-red-500 hover:text-red-400 uppercase tracking-widest">Sil</button>
                </div>
              </div>
            ) : (
              <div className="text-center">
                <FileText className={`w-6 h-6 ${colors.textTertiary} mx-auto mb-2`} />
                <span className={`text-[10px] font-bold ${colors.textTertiary} uppercase tracking-widest block`}>PDF Rapor</span>
                <input type="file" accept="application/pdf" onChange={(e) => handleGenericFileUpload(e, 'tanitaPdfUrl')} className="absolute inset-0 opacity-0 cursor-pointer" />
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* Background Overlay */}
      <div 
        className={`absolute inset-0 ${isDark ? 'bg-zinc-950/70' : 'bg-slate-900/40'} backdrop-blur-sm`} 
        onClick={onClose}
      />
      
      {/* Modal Container */}
      <div className={`relative w-full max-w-4xl max-h-[85vh] ${colors.cardBg} rounded-lg shadow-xl border ${colors.border} flex flex-col overflow-hidden`}>
        
        {/* Header */}
        <div className={`px-6 py-4 border-b ${colors.border} flex items-center justify-between ${isDark ? 'bg-zinc-900/50' : 'bg-slate-50'}`}>
          <div>
            <h3 className={`text-base font-bold ${colors.textPrimary}`}>
              {editingMeasurement ? t('studentProfile.measurements.modal.editTitle') : t('studentProfile.measurements.modal.addTitle')}
            </h3>
            <p className={`text-[11px] ${colors.textTertiary} font-medium`}>
              {student?.name} {student?.surname}
            </p>
          </div>
          <button
            onClick={onClose}
            className={`p-1.5 rounded ${colors.bgHover} transition-colors ${colors.textSecondary}`}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Tabs */}
        <div className={`px-6 border-b ${colors.border}`}>
          <nav className="flex space-x-8">
            {tabs.map((tab) => {
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-3 px-1 text-xs font-bold uppercase tracking-wider transition-all relative ${
                    isActive ? 'text-violet-500' : `${colors.textTertiary} ${colors.bgHover}`
                  }`}
                >
                  {tab.name}
                  {isActive && (
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-violet-500"></div>
                  )}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto p-8">
          {activeTab === 'body' && renderBodyMeasurements()}
          {activeTab === 'caliper' && renderCaliperMeasurements()}
          {activeTab === 'tanita' && renderTanitaMeasurements()}
        </div>

        {/* Footer */}
        <div className={`px-6 py-4 border-t ${colors.border} flex items-center justify-end space-x-3 ${isDark ? 'bg-zinc-900/50' : 'bg-slate-50'}`}>
          <button
            onClick={onClose}
            disabled={saving}
            className={`px-4 py-2 text-xs font-bold uppercase tracking-wider ${colors.textSecondary} ${colors.bgHoverStrong} transition-colors disabled:opacity-50`}
          >
            {t('studentProfile.measurements.modal.cancel')}
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-2 bg-violet-600 hover:bg-violet-700 text-white text-xs font-bold uppercase tracking-wider rounded transition-colors shadow-lg shadow-violet-900/20 disabled:opacity-50 flex items-center gap-2"
          >
            {saving ? (
              <>
                <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                <span>KAYDEDİLİYOR...</span>
              </>
            ) : (
              <span>{editingMeasurement ? t('studentProfile.measurements.modal.update') : t('studentProfile.measurements.modal.save')}</span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default StudentMeasurementModal;